import {
  Box,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { Trash, UserCircle } from '@phosphor-icons/react';
import React, { useCallback, useState } from 'react';
import { getAPIErrorMessage } from '../../../helpers/helpers';
import { ThirdPartyIntegrationService } from '../../../service/ThirdPartyIntegrationService';
import {
  NotificationType,
  useNotificationStore,
} from '../../../store/storeNotifications';
import { useStoreThirdPartyIntegration } from '../../../store/storeThirdPartyIntegration';
import type { IntegrationProviderConfig } from '../../ThirdPartyIntegration/types';

interface ConnectionListPanelProps {
  config: IntegrationProviderConfig;
  isDark: boolean;
}

const ConnectionListPanel: React.FC<ConnectionListPanelProps> = ({
  config,
  isDark,
}) => {
  const setNotification = useNotificationStore(state => state.setNotification);
  const { fetchConnections, removeConnection, setConnectionSharing } =
    useStoreThirdPartyIntegration();
  const connections = useStoreThirdPartyIntegration(state =>
    state.getConnections(config.id),
  );
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);

  const subtitleColor = isDark ? '#98A2B3' : '#667085';
  const titleColor = isDark ? '#F9FAFB' : '#101828';
  const dividerColor = isDark ? 'rgba(71, 84, 103, 0.4)' : '#EAECF0';
  const sharingSupported = config.supportsSharing !== false;

  const handleDisconnect = useCallback(
    async (integrationId: string) => {
      setDisconnectingId(integrationId);
      try {
        await ThirdPartyIntegrationService.disconnect(config.id, integrationId);
        removeConnection(config.id, integrationId);
        setNotification({
          type: NotificationType.Success,
          message: `${config.name} connection removed.`,
        });
      } catch (error) {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error),
        });
      } finally {
        setDisconnectingId(null);
      }
    },
    [config.id, config.name, removeConnection, setNotification],
  );

  const handleToggleSharing = useCallback(
    async (integrationId: string, shared: boolean) => {
      try {
        await setConnectionSharing(config.id, integrationId, shared);
        setNotification({
          type: NotificationType.Success,
          message: shared
            ? 'Connection shared with your organization.'
            : 'Connection is now private.',
        });
      } catch (error) {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error),
        });
        fetchConnections(config.id);
      }
    },
    [config.id, setConnectionSharing, fetchConnections, setNotification],
  );

  if (connections.length === 0) {
    return null;
  }

  return (
    <Box>
      <Typography
        sx={{
          fontSize: '12px',
          fontWeight: 600,
          color: subtitleColor,
          textTransform: 'uppercase',
          letterSpacing: '0.4px',
          mb: 1,
        }}
      >
        Connections
      </Typography>
      <Stack
        sx={{
          border: `1px solid ${dividerColor}`,
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        {connections.map((connection, index) => {
          const isLastRow = index === connections.length - 1;
          return (
            <Box
              key={connection.id}
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: isLastRow ? 'none' : `1px solid ${dividerColor}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  backgroundColor: isDark
                    ? 'rgba(127, 86, 217, 0.18)'
                    : 'rgba(105, 65, 198, 0.10)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <UserCircle
                  size={18}
                  color={isDark ? '#D6BBFB' : '#6941C6'}
                  weight='bold'
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: titleColor,
                    lineHeight: 1.3,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {connection.name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '11px',
                    color: subtitleColor,
                  }}
                >
                  {connection.isOwn
                    ? 'You'
                    : `Shared by ${connection.ownerUserName}`}
                </Typography>
              </Box>
              {connection.isOwn && (
                <Tooltip
                  title={
                    sharingSupported
                      ? 'Share this connection with everyone in your organization'
                      : 'Sharing available after legacy migration'
                  }
                  arrow
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '11px',
                        color: subtitleColor,
                        userSelect: 'none',
                      }}
                    >
                      Share
                    </Typography>
                    <Switch
                      size='small'
                      checked={!!connection.isSharedWithOrg}
                      disabled={!sharingSupported}
                      onChange={event =>
                        handleToggleSharing(connection.id, event.target.checked)
                      }
                    />
                  </Box>
                </Tooltip>
              )}
              {connection.isOwn && (
                <IconButton
                  size='small'
                  disabled={disconnectingId === connection.id}
                  onClick={() => handleDisconnect(connection.id)}
                  aria-label={`Disconnect ${connection.name}`}
                  sx={{
                    color: subtitleColor,
                    '&:hover': {
                      color: '#EF4444',
                      backgroundColor: isDark
                        ? 'rgba(239, 68, 68, 0.1)'
                        : 'rgba(239, 68, 68, 0.06)',
                    },
                  }}
                >
                  <Trash size={14} />
                </IconButton>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default ConnectionListPanel;
